"use client";
import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Image from "next/image";
import { useTranslation } from "react-i18next";

// ─── Assets ─────────────────────────────────────────────────────────────────
const CORRECT_ICON =
  "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/Correct_leap-Updated.webp";
const CROSS_ICON =
  "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/Cross_leap_Updated.webp";

// ─── Styled Components ──────────────────────────────────────────────────────
const SectionWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: "#F4F3F9",
  padding: "72px 0",
  [theme.breakpoints.down("sm")]: {
    padding: "48px 0",
  },
}));

const TextWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  textAlign: "center",
  maxWidth: "700px",
  margin: "0 auto 48px auto",
  [theme.breakpoints.down("sm")]: {
    textAlign: isRTL ? "right" : "left",
    margin: "0 0 32px 0",
  },
}));

const Eyebrow = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "16px",
  fontWeight: 700,
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "#9048FB",
  marginBottom: "16px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
  "&::before": {
    content: '""',
    display: "inline-block",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#9048FB",
  },
}));

const SectionHeading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontWeight: 700,
  fontSize: "35px",
  lineHeight: 1.3,
  color: "#171522",
  marginBottom: "12px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "25px",
  },
}));

const SectionSubtext = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "16px",
  lineHeight: 1.7,
  color: "#5A5568",
}));

// ─── Outer wrapper: full width, forces horizontal centering regardless of
// parent RTL/LTR quirks (doesn't rely on margin:auto alone) ──────────────────
const CardsRowOuter = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  width: "100%",
}));

// ─── Cards row: plain flexbox (RTL-safe, theme.direction pe depend nahi karta) ──
const CardsRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  display: "flex",
  flexDirection: isRTL ? "row-reverse" : "row",
  alignItems: "stretch",
  gap: "24px",
  width: "100%",
  maxWidth: "900px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
}));

const CardCol = styled(Box)(({ theme }) => ({
  flex: "1 1 0",
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    flex: "1 1 auto",
    width: "100%",
  },
}));

const CardBase = styled(Box)(({ theme }) => ({
  borderRadius: "20px",
  padding: "32px 40px",
  height: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: "24px",
  },
}));

const OldWayCard = styled(CardBase)(() => ({
  backgroundColor: "#FFFFFF",
  border: "1px solid #ECEBF3",
  boxShadow: "0 1px 2px rgba(17,17,17,0.03), 0 8px 24px rgba(17,17,17,0.03)",
}));

const GetGeekWayCard = styled(CardBase)(() => ({
  backgroundColor: "#151022",
  border: "1px solid #2A2340",
  boxShadow: "0 20px 45px rgba(21,16,34,0.28), 0 4px 12px rgba(21,16,34,0.2)",
}));

const CardTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "variant2" && prop !== "isRTL",
})(({ variant2, isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: variant2 ? "#AD78FB" : "#46475D",
  marginBottom: "16px",
}));

const PointRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isLast",
})(({ isLast }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "10px 0",
  borderBottom: isLast ? "none" : "1px solid #EFEAF6",
}));

const PointRowDark = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isLast",
})(({ isLast }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "10px 0",
  borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.08)",
}));

const IconWrapper = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  minWidth: "20px",
}));

const PointText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "variant2" && prop !== "isRTL",
})(({ theme, variant2, isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "16px",
  lineHeight: 1.6,
  color: variant2 ? "#E5E2EE" : "#5A5568",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

// ─── Component ───────────────────────────────────────────────────────────────
const AIInHRComparisonSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const oldWayPoints = t("aiInHRComparisonSection.oldWay.points", { returnObjects: true });
  const getgeekWayPoints = t("aiInHRComparisonSection.getgeekWay.points", { returnObjects: true });

  return (
    <SectionWrapper>
      <Container maxWidth="lg">
        <TextWrapper isRTL={isRTL}>
          <Eyebrow isRTL={isRTL}>{t("aiInHRComparisonSection.eyebrow")}</Eyebrow>
          <SectionHeading variant="h2" isRTL={isRTL}>
            {t("aiInHRComparisonSection.heading")}
          </SectionHeading>
          <SectionSubtext isRTL={isRTL}>
            {t("aiInHRComparisonSection.subtext")}
          </SectionSubtext>
        </TextWrapper>

        {/* Outer Box full width justifyContent:center + inner row-reverse for RTL —
            do-layer centering taaki parent ki kisi bhi RTL/width quirk se na tute */}
        <CardsRowOuter>
          <CardsRow isRTL={isRTL}>
            <CardCol>
              <OldWayCard>
                <CardTitle isRTL={isRTL}>{t("aiInHRComparisonSection.oldWay.title")}</CardTitle>
                {oldWayPoints.map((point, index) => (
                  <PointRow key={index} isLast={index === oldWayPoints.length - 1}>
                    <IconWrapper>
                      <Image src={CROSS_ICON} alt="cross" width={20} height={20} />
                    </IconWrapper>
                    <PointText isRTL={isRTL}>{point}</PointText>
                  </PointRow>
                ))}
              </OldWayCard>
            </CardCol>

            <CardCol>
              <GetGeekWayCard>
                <CardTitle variant2 isRTL={isRTL}>
                  {t("aiInHRComparisonSection.getgeekWay.title")}
                </CardTitle>
                {getgeekWayPoints.map((point, index) => (
                  <PointRowDark key={index} isLast={index === getgeekWayPoints.length - 1}>
                    <IconWrapper>
                      <Image src={CORRECT_ICON} alt="check" width={20} height={20} />
                    </IconWrapper>
                    <PointText variant2 isRTL={isRTL}>{point}</PointText>
                  </PointRowDark>
                ))}
              </GetGeekWayCard>
            </CardCol>
          </CardsRow>
        </CardsRowOuter>
      </Container>
    </SectionWrapper>
  );
};

export default AIInHRComparisonSection;